import { getISOWeek, getISOWeekYear, format as formatDateFns }  from "date-fns";
import { Gender, Role, PatientStatusCode, GlucoseFilesThreshold } from '../config/DiactiveTypes';

export const calculateAge = (dateOfBirth, roundToQuarter = false) => {
  const now = new Date();
  const millisPerYear = 31556952000; // Approximately 365.25 days in milliseconds (considering leap years)

  const exactAge = (now - dateOfBirth) / millisPerYear;
  return roundToQuarter ? Math.round(exactAge * 4) / 4 : Math.floor(exactAge);
};

export const calculateMaturationPHV = (gender, dateOfBirth, height) => {
  const emptyResult = { maturation: 0, phv: 0 };
  if (gender !== Gender.M && gender !== Gender.F) return emptyResult;
  if (!dateOfBirth || !height || isNaN(height) || height <= 0) return emptyResult;

  const genderConstant = gender === Gender.M ? -7.999994 : -7.709133;
  const ageHeightCoefficient = gender === Gender.M ? 0.0036124 : 0.0042232;
  const age = calculateAge(dateOfBirth, true);
  const maturation = genderConstant + ageHeightCoefficient * age * height;
  const phv = age + maturation;
  return { maturation, phv };
};

export const getMinDOB = () => {
  const yearDelta = 22; // Because in 2025 it's 2003
  return `${new Date().getFullYear() - yearDelta}-01-01`;
};

export const centileRankToMuscleStrength = (centileRank) => {
  if (centileRank >= 80) return "Alto";
  else if (centileRank >= 20) return "Medio";
  else return "Bajo";
};

export const getPatientHspId = (patient) => {
  const patientNumber = patient.patientNumber;
  const patientNumberStr = patientNumber.toString().padStart(3, '0');
  return `${patient.hospital}_${patientNumberStr}`;
};

export const getPatientFullName = (patient) => {
  if (patient.name) return patient.name; // For legacy patients with name field

  return `${patient.firstName} ${patient.lastName}`;
};

export const getPatientGroupName = (role) => {
  switch (role) {
    case Role.Intr25:
      return "Intervención";
    case Role.Ctrl25:
      return "Control";
    default:
      return "";
  }
};

export const totalDurationDays = 90;
export const minDaysForPostEval = 7;

export const calculateDaysRemaining = (countdownStartedAt, isInStudy) => {
  if (!isInStudy) return 0;
  if (countdownStartedAt === undefined) return totalDurationDays;

  const currentDate = new Date();
  const currentMidnight = new Date(currentDate.toDateString());

  const midnightCountdownStartedAt = new Date(countdownStartedAt.toDate().toDateString());

  const daysSinceCreation = Math.floor((currentMidnight - midnightCountdownStartedAt) / (1000 * 60 * 60 * 24));
  return Math.max(0, totalDurationDays - daysSinceCreation);
};

export const calculatePostEvalDate = (countdownStartedAt) => {
  if (countdownStartedAt === undefined) return null;

  const startMidnight = new Date(countdownStartedAt.toDate().toDateString());
  const postEvalDate = new Date(startMidnight);
  postEvalDate.setDate(postEvalDate.getDate() + totalDurationDays);
  return postEvalDate;
};

export const determinePatientStatus = (
  countdownStartedAt,
  daysRemaining,
  hasFilledQsPre,
  hasFilledQsPost,
  glucoseFileCount,
  isInStudy
) => {
  const minDaysWarning = 15;
  const minDaysCritical = 7;

  if (!isInStudy) return { code: PatientStatusCode.WITHDRAWN, color: 'disabled', label: 'Dado/a de baja' };

  // Check pre-phase requirements
  if (countdownStartedAt === undefined && (!hasFilledQsPre || (glucoseFileCount.pre ?? 0) < GlucoseFilesThreshold)) {
    const pendingItems = [];
    if (!hasFilledQsPre) pendingItems.push('cuestionarios');
    if ((glucoseFileCount.pre ?? 0) < GlucoseFilesThreshold) pendingItems.push('archivos');

    return {
      code: PatientStatusCode.PRE_PENDING,
      color: 'error',
      label: `Pre: ${pendingItems.join(' y ')} pendientes`
    };
  }

  if (
    countdownStartedAt === undefined &&
    hasFilledQsPre &&
    (glucoseFileCount.pre ?? 0) >= GlucoseFilesThreshold
  ) {
    return {
      code: PatientStatusCode.COUNTDOWN_NOT_STARTED,
      color: 'error',
      label: 'Cuenta atrás no iniciada'
    };
  }

  // Check post-phase requirements
  if (daysRemaining === 0 && (!hasFilledQsPost || (glucoseFileCount.post ?? 0) < GlucoseFilesThreshold)) {
    const pendingItems = [];
    if (!hasFilledQsPost) pendingItems.push('cuestionarios');
    if ((glucoseFileCount.post ?? 0) < GlucoseFilesThreshold) pendingItems.push('archivos');

    return {
      code: PatientStatusCode.POST_PENDING,
      color: 'error',
      label: `Post: ${pendingItems.join(' y ')} pendientes`
    };
  }

  if (daysRemaining === 0 && hasFilledQsPost && (glucoseFileCount.post ?? 0) >= GlucoseFilesThreshold) {
    return {
      code: PatientStatusCode.STUDY_FINISHED,
      color: 'disabled',
      label: 'Estudio terminado'
    };
  }

  if (daysRemaining > 0 && daysRemaining <= minDaysCritical) {
    return {
      code: PatientStatusCode.LOW_DAYS_REMAINING,
      color: 'error',
      label: `Menos de ${minDaysCritical} días restantes`
    };
  }

  if (daysRemaining > 0 && daysRemaining <= minDaysWarning) {
    return {
      code: PatientStatusCode.LOW_DAYS_REMAINING,
      color: 'warning',
      label: `Menos de ${minDaysWarning} días restantes`
    };
  }

  if (daysRemaining > minDaysWarning) {
    return {
      code: PatientStatusCode.NORMAL,
      color: 'success',
      label: 'Estado normal'
    };
  }

  return {
    code: PatientStatusCode.UNDETERMINED,
    color: 'disabled',
    label: 'Estado indeterminado'
  };
};

export const calculateWorkoutsPerWeek = (workouts) => {
  let workoutsPerWeek = 0;
  if (workouts.length > 1) {
    const firstWorkout = workouts[0]; // First in asc order = first chronologically
    const lastWorkout = workouts[workouts.length - 1]; // Last in asc order = most recent

    const daysBetween = Math.floor(
      (lastWorkout.createdAt.toDate() - firstWorkout.createdAt.toDate()) / (1000 * 60 * 60 * 24)
    );

    if (daysBetween > 0) {
      const weeksBetween = daysBetween / 7;
      workoutsPerWeek = Math.round((workouts.length / weeksBetween) * 100) / 100;
    }
  }
  return workoutsPerWeek;
};

export const isEmpty = (value) => {
  return value === undefined || value === null || value === "" || Number.isNaN(value);
};

export const determineDevicePlatform = (deviceOS) => {
  const platforms = ["Android", "iOS"];
  for (const platform of platforms) {
    if (deviceOS.includes(platform)) return platform;
  }
};

export const formatTimestamp = (timestamp) => {
  return formatDate(timestamp.toDate());
};

export const formatDateTime = (value, pattern = 'dd/MM/yyyy HH:mm') => {
  if (!value) return '—';

  const date =
    value?.toDate instanceof Function
      ? value.toDate()
      : (value instanceof Date ? value : new Date(value));

  if (!date || Number.isNaN(date.getTime())) return '—';
  return formatDateFns(date, pattern);
};

export const formatDate = (date) => {
  return new Intl.DateTimeFormat('en-GB').format(date);
};

export const timestampToLocalDate = (timestamp) => {
  const date = timestamp.toDate();
  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date - timezoneOffset);
};

export const getSessionWeekNumber = (firstDate, sessionDate) => {
  const firstWeek = getISOWeek(firstDate);
  const firstYear = getISOWeekYear(firstDate);

  const sessionWeek = getISOWeek(sessionDate);
  const sessionYear = getISOWeekYear(sessionDate);

  return (sessionYear - firstYear) * 52 + (sessionWeek - firstWeek) + 1;
};

export const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date =
    value?.toDate instanceof Function
      ? value.toDate()
      : (value instanceof Date ? value : new Date(value));
  if (!date || Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const parseDateTimeLocalValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};